"use client";

import { BookOpen, Search, Terminal } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import { Spinner } from "@/components/states/spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  NavCard,
  PageHeader,
  primaryCta,
} from "@/features/docs/components/doc-primitives";
import { searchDocs } from "@/features/docs/lib/search";
import type { SearchIndex } from "@/features/docs/types";

/**
 * The prebuilt index (`scripts/generate-docs.ts` → `public/docs/`). It is
 * fetched rather than built in-code because the automation half is scraped
 * from the page sources at build time — the app has no way to read those at
 * runtime, so an in-code index could only ever know each automation page's
 * title and description, and method names like `swipePoly` were unfindable.
 *
 * Fetching also takes the whole v1 + v2 endpoint dataset out of the client
 * bundle: this is a `"use client"` module, so importing `buildSearchIndex`
 * pulled all ~5k lines of it in for every visitor to /docs/search.
 */
const INDEX_URL = "/docs/search-index.json";

/**
 * A file path rendered as an inline code chip. Underlined at rest, not just on
 * hover: an inline link set apart from its surrounding text by colour alone
 * fails WCAG (axe `link-in-text-block`), and the chip's border reads as
 * decoration rather than as affordance.
 */
function PathChip({ href, children }: { href: string; children: string }) {
  return (
    <a
      href={href}
      className="inline-block rounded-[5px] border border-hairline bg-elevated px-1.5 py-0.5 font-mono text-[0.75rem] text-docs-link underline decoration-docs-link/40 underline-offset-2 transition-colors hover:border-docs-link/40 hover:decoration-docs-link focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      {children}
    </a>
  );
}

export function DocsSearchView() {
  const params = useSearchParams();
  const initial = params.get("q") ?? "";
  const [query, setQuery] = useState(initial);
  const [submitted, setSubmitted] = useState(initial);

  const [index, setIndex] = useState<SearchIndex | null>(null);
  const [indexFailed, setIndexFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(INDEX_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then((data: SearchIndex) => {
        if (!cancelled) setIndex(data);
      })
      .catch(() => {
        if (!cancelled) setIndexFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const results = useMemo(
    () => (submitted && index ? searchDocs(index, submitted) : []),
    [index, submitted],
  );
  const loading = Boolean(submitted) && !index && !indexFailed;

  return (
    <div>
      {/* No description here on purpose: the two helper lines below the input
       * say the same thing, and the reference puts them there. A subtitle would
       * be the same sentence twice on first paint.
       *
       * mb-7 rather than the hero default mb-14: with no description between
       * them, the rule and the search bar are one unit, and 56px of air read as
       * the title being unrelated to the field it labels. */}
      <PageHeader hero title="Search Documentation" className="mb-7" />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(query.trim());
        }}
        // No top margin: it would collapse against the header's mb-7 and change
        // nothing. The gap above is the header's to own.
        className="flex gap-2.5"
        role="search"
      >
        {/* The glow lives on the wrapper via focus-within, not on the input:
         * the icon sits inside the same rounded box, so a ring on the input
         * alone would draw a seam between them. */}
        <div className="group relative flex-1 rounded-xl transition-shadow duration-150 focus-within:shadow-[0_0_0_4px_hsl(var(--primary)/0.18)]">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-primary-text"
          />
          <Input
            type="search"
            aria-label="Search documentation"
            placeholder="Search endpoints, actions, guides…"
            // Hover/focus move the boundary onto solid --nav-active (5.21:1 on
            // --card). They used to swap --input for --primary/25 and --primary,
            // which measured 1.2:1 and 2.47:1 — so interacting with the box made
            // its boundary WORSE than the 3.91:1 it rests at, and `ring-0` left
            // nothing else to carry the focus cue.
            className="h-11 rounded-xl border-input bg-card pl-10 pr-3.5 text-[0.9375rem] transition-colors hover:border-nav-active focus-visible:border-nav-active focus-visible:ring-0 focus-visible:ring-offset-0"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            // Deliberate: focus the search box on the dedicated search page.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </div>
        <Button type="submit" className={`h-11 px-7 ${primaryCta}`}>
          Search
        </Button>
      </form>

      {/* Empty state — shown only before a search, as in the reference.
       *
       * Both lines sit at full `--muted-foreground`; the second is separated by
       * SIZE, not opacity. `text-muted-foreground` reads as the obvious way
       * to de-emphasise it and measures 2.72:1 light / 4.31:1 dark — under AA in
       * both themes. Light `--muted-foreground` is already 4.74:1, so there is
       * no headroom to dim it at all. */}
      {!submitted ? (
        <div className="pb-7 pt-9 text-center">
          <p className="text-[0.9375rem] text-muted-foreground">
            Enter a search term to find documentation pages.
          </p>
          <p className="mt-2.5 text-[0.8125rem] text-muted-foreground">
            Search across API documentation and Automation guides.
          </p>
        </div>
      ) : null}

      {loading ? <Spinner className="mx-auto my-12" /> : null}

      {/* Same failure contract as /docs/llm.txt: say the artifact is missing
       * and how to make it, rather than rendering "no results" for every query
       * and letting it read as an empty corpus. */}
      {indexFailed ? (
        <div className="mt-6 rounded-xl border border-hairline bg-elevated p-4">
          <p className="text-[0.8125rem] leading-relaxed text-muted-foreground">
            <span className="font-semibold text-warning-text">
              Search index unavailable.
            </span>{" "}
            The docs index is a build artifact. Run{" "}
            <code className="font-mono text-foreground">
              npm run docs:generate
            </code>{" "}
            to regenerate it.
          </p>
        </div>
      ) : null}

      {submitted && !loading && !indexFailed && results.length === 0 ? (
        <div className="pb-7 pt-9 text-center">
          <p className="text-[0.9375rem] text-muted-foreground">
            No results for &ldquo;{submitted}&rdquo;.
          </p>
          <p className="mt-2.5 text-[0.8125rem] text-muted-foreground">
            Try different keywords or check your spelling.
          </p>
        </div>
      ) : null}

      {results.length > 0 ? (
        <div className="mt-6 space-y-3">
          {results.map((r) => (
            <Link
              key={r.page.id}
              href={r.page.path}
              className="group block rounded-xl border border-hairline bg-card p-4 shadow-[0_10px_24px_-20px_rgb(0_0_0/0.5)] transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="flex items-center gap-1.5 font-semibold text-foreground">
                  {r.page.title}
                  <span
                    aria-hidden="true"
                    className="text-xs text-muted-foreground transition-all duration-200 group-hover:translate-x-1 group-hover:text-primary-text"
                  >
                    →
                  </span>
                </span>
                <span className="shrink-0 font-mono text-xs text-muted-foreground">
                  {r.page.path}
                </span>
              </div>
              <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                {r.snippet}
              </p>
            </Link>
          ))}
        </div>
      ) : null}

      <section className="mt-8 border-t border-hairline pt-6">
        <h2 className="text-xl font-bold tracking-tight">
          Browse Documentation
        </h2>
        {/* Same NavCard language as the overview's "Start here", so arriving
         * here from a dead-end search doesn't change visual gear. The two
         * targets mirror the reference's Browse Documentation block: /docs is
         * the API overview (the v2 client API plus the v1 entry point), and
         * /docs/automation is the automation landing — not /docs/v1, which is
         * only the router index one level down. */}
        <div className="mt-5 grid items-stretch gap-4 sm:grid-cols-2">
          <NavCard
            title="API Documentation"
            description="Jobs, Tasks, Payments, Users, and Devices APIs"
            href="/docs"
            icon={Terminal}
            accent="brand"
          />
          <NavCard
            title="Automation Documentation"
            description="Guides and API reference for Android automation"
            href="/docs/automation"
            icon={BookOpen}
            accent="sky"
          />
        </div>
      </section>

      {/* Mirrors the reference's "For LLMs" block, with v5's real artifacts.
       *
       * The reference points at /docs/docs.txt and /docs/docs-full.txt; both
       * 404 here, so copying it verbatim would ship dead links. v5 exposes the
       * v1 reference through the /docs/v1/llm.txt route instead.
       *
       * /docs/llm-docs.txt is deliberately NOT listed: it is a static file in
       * public/ holding the same buildLlmText() output as the password-gated
       * route, so linking it would advertise a way around that gate. */}
      <section className="mt-6 rounded-xl border border-hairline bg-elevated p-4">
        {/* Each chip is glued to its trailing punctuation with a nowrap span.
         * An inline-block chip offers a line-break opportunity after it, so the
         * comma and full stop otherwise wrap to the next line on their own. */}
        <p className="text-[0.8125rem] leading-[1.9] text-muted-foreground">
          <span className="font-semibold text-foreground">For LLMs:</span>{" "}
          access the v2 Client API and automation docs as plain text at{" "}
          <span className="whitespace-nowrap">
            <PathChip href="/docs/llm.txt">/docs/llm.txt</PathChip>,
          </span>{" "}
          the v1 API reference at{" "}
          <span className="whitespace-nowrap">
            <PathChip href="/docs/v1/llm.txt">/docs/v1/llm.txt</PathChip>,
          </span>{" "}
          or the structured search index at{" "}
          <span className="whitespace-nowrap">
            <PathChip href="/docs/search-index.json">
              /docs/search-index.json
            </PathChip>
            .
          </span>{" "}
          Prefer either over fetching each page individually.
        </p>
      </section>
    </div>
  );
}
